/**
 * MultiAgentCredentialFields — the credential body for a MULTI-agent template.
 *
 * A marketplace template's graph can pin DIFFERENT agents on different nodes
 * (e.g. a planner on Claude, a builder on Codex). The marketplace sync stamps
 * the distinct set onto the row as `requiredAgents` (e.g. ['claude','codex']).
 * Each of those agents is a separate vendor with its OWN API key, so a single
 * "agent + model + key" row can't cover the deploy — the user must supply one
 * credential per required agent.
 *
 * Rather than re-implement the field markup, this renders ONE <AgentModelFields>
 * per required agent, each LOCKED to that agent (its options filtered to that
 * vendor only). That reuses all the existing behavior — vendor label, model
 * select, masked-credential handling, per-vendor validation + help copy — so
 * every slot looks and behaves exactly like the single-agent form.
 *
 * Fully controlled: the parent owns two maps keyed by agent —
 *   modelsByAgent  { [agent]: '<agent>:<model>' }
 *   keysByAgent    { [agent]: apiKeyString }
 * — and gets granular onChange callbacks. The parent builds the deploy body
 * (agentTokens map) + gates Save on validity.
 */

import React from 'react';
import PropTypes from 'prop-types';
import AgentModelFields from './AgentModelFields';
import { filterOptionsByAgents } from '../../../utils/agent-options';

const AGENT_LABEL = {
  claude: 'Claude (Anthropic)',
  codex: 'Codex (OpenAI)',
  gemini: 'Gemini (Google)',
};

export default function MultiAgentCredentialFields({
  requiredAgents = [],
  agentOptions = [],
  modelsByAgent = {},
  onModelChange,
  keysByAgent = {},
  onKeyChange,
  submitted = false,
  serverErrorsByAgent = {},
  disabled = false,
  idPrefix = 'agent-model',
}) {
  return (
    <div className="multi-agent-credential-fields">
      {requiredAgents.map((agent) => (
        <fieldset
          key={agent}
          className="multi-agent-credential-slot"
          // Group each agent's model + key visually; native fieldset keeps it
          // accessible without adding new chrome/styles.
          style={{ border: 'none', padding: 0, margin: '0 0 12px 0' }}
        >
          <legend className="apps-modal-field-label" style={{ padding: 0 }}>
            {AGENT_LABEL[agent] || agent}
          </legend>
          <AgentModelFields
            // Options filtered to THIS agent → the model select is effectively
            // locked to the required vendor (no cross-agent switching).
            agentOptions={filterOptionsByAgents(agentOptions, [agent])}
            value={modelsByAgent[agent] || ''}
            onValueChange={(v) => onModelChange?.(agent, v)}
            apiKey={keysByAgent[agent] || ''}
            onApiKeyChange={(v) => onKeyChange?.(agent, v)}
            submitted={submitted}
            serverKeyError={serverErrorsByAgent[agent] || ''}
            disabled={disabled}
            idPrefix={`${idPrefix}-${agent}`}
          />
        </fieldset>
      ))}
    </div>
  );
}

MultiAgentCredentialFields.propTypes = {
  requiredAgents: PropTypes.arrayOf(PropTypes.string),
  agentOptions: PropTypes.array,
  modelsByAgent: PropTypes.object,
  onModelChange: PropTypes.func,
  keysByAgent: PropTypes.object,
  onKeyChange: PropTypes.func,
  submitted: PropTypes.bool,
  serverErrorsByAgent: PropTypes.object,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string,
};
